import { OTPInput } from '@neo/blocks/otp-input-with-slots'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { VERIFY_APPLICATION_OTP_EXPIRY_TIME } from 'v2/configs/constants'
import { ElapsedTimer } from 'v2/shared/components/elapsed-timer'
import { AUTH_LINK_CLASS } from './auth-styles'

interface TwoFactorCodeInputProps {
  code: string
  onChange: (value: string) => void
  isAuthenticatorMode: boolean
  phoneNumber: string
  otpChannel?: string
  timerStartTime: string | null
  isSmsResending: boolean
  isVoiceResending: boolean
  onResendCode: () => void
  onResendViaCall: () => void
  onTimerStop: () => void
  onComplete?: (value: string) => void
}

const redactPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.length <= 4) {
    return phoneNumber
  }

  const parsedNumber = parsePhoneNumberFromString(phoneNumber)

  if (!parsedNumber) {
    return phoneNumber
  }

  const { nationalNumber } = parsedNumber
  const countryCode = parsedNumber.countryCallingCode

  if (nationalNumber.length <= 4) {
    return parsedNumber.formatInternational()
  }

  // Show only country code and last 4 digits, redact everything else
  const last4Digits = nationalNumber.slice(-4)
  const redactedLength = nationalNumber.length - 4
  const asterisks = Array(redactedLength).fill('*').join('')

  return `+${countryCode}${asterisks}${last4Digits}`
}

const TwoFactorCodeInput = ({
  code,
  onChange,
  isAuthenticatorMode,
  phoneNumber,
  otpChannel,
  timerStartTime,
  isSmsResending,
  isVoiceResending,
  onResendCode,
  onResendViaCall,
  onTimerStop,
  onComplete,
}: TwoFactorCodeInputProps) => {
  // Function to get the appropriate resend button text based on state
  const getResendButtonText = () => {
    if (timerStartTime) {
      return (
        <>
          Resend Code (
          <ElapsedTimer
            startTime={timerStartTime}
            countdownDuration={Number(VERIFY_APPLICATION_OTP_EXPIRY_TIME)}
            format="s"
            onStop={onTimerStop}
          />
          s)
        </>
      )
    }
    if (isSmsResending) {
      return 'Sending...'
    }
    return 'Resend Code'
  }

  return (
    <div>
      {!isAuthenticatorMode && (
        <div className="mb-4">
          <div className="text-p-500">{redactPhoneNumber(phoneNumber)}</div>
          {otpChannel && (
            <p className="text-xs-400 text-muted-foreground mt-1">OTP sent via {otpChannel}</p>
          )}
        </div>
      )}
      <label htmlFor="verification-code" className="mb-1 block text-s-500 text-secondary">
        Verification code
      </label>
      <OTPInput
        maxLength={6}
        value={code}
        onChange={onChange}
        data-testid="input-verification-code"
        className="mb-0"
        onComplete={onComplete}
      />
      {isAuthenticatorMode ? null : (
        <div className="mt-2 mb-4 flex items-center text-xs-400">
          <button
            type="button"
            className={`${AUTH_LINK_CLASS} text-s-500 ${
              isSmsResending || !!timerStartTime ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={onResendCode}
            disabled={isSmsResending || !!timerStartTime}
            data-testid="button-resend-code"
          >
            {getResendButtonText()}
          </button>
          <span className="px-2 text-s-500 text-muted-foreground">or</span>
          <button
            type="button"
            className={`${AUTH_LINK_CLASS} text-s-500 ${
              isVoiceResending ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={onResendViaCall}
            disabled={isVoiceResending}
            data-testid="button-send-call"
          >
            {isVoiceResending ? 'Sending...' : 'Request via a Voice Call'}
          </button>
        </div>
      )}
    </div>
  )
}

export { TwoFactorCodeInput }
